<template>
    <div class="simulation-wizard">
        <div class="wizard-content">
            <div class="header">
                <p class="header__text" @click="restartWizard"><i class="fa-regular fa-chevron-left"></i>{{
                    $t(wizardTypes[type].goBackText) }}</p>
                <Stepper :steps="steps" :activeStep="currentStep" :setCurrentStep="setCurrentStep" :maxStep="maxStep">
                </Stepper>
            </div>
            <div class="body">
                <transition-group name="slide" mode="out-in" tag="div" class="steps-container">
                    <!-- Funding Steps -->
                    <template style="height: fit-content" v-if="type === 'funding'">
                        <ChooseMarket :setCurrentMarket="setCurrentMarket" :key="1" :stepActive="this.currentStep == 1"
                            :setNextBtnDisabled="setNextBtnDisabled" :currentMarket="currentMarket"></ChooseMarket>
                        <TestToTakeVue :stepActive="this.currentStep == 2" :key="2" :vendors="challenges"
                            :setNextBtnDisabled="setNextBtnDisabled" :setCurrentPhase="setCurrentPhase"
                            :setCurrentVendor="setCurrentVendor" :setCurrentChallenge="setCurrentChallenge"
                            :currentVendor="currentVendor" :currentPhase="currentPhase"
                            :currentChallenge="currentChallenge">
                        </TestToTakeVue>
                        <ChooseBroker :stepActive="this.currentStep == 3" :key="3" :currentBroker="currentBroker"
                            :setCurrentBroker="setCurrentBroker" :setNextBtnDisabled="setNextBtnDisabled">
                        </ChooseBroker>
                        <TestSummary :stepActive="this.currentStep == 4" :key="4" :currentVendor="currentVendor"
                            :currentBroker="currentBroker" :currentChallenge="currentChallenge"
                            :currentPhase="currentPhase" :setNextBtnDisabled="setNextBtnDisabled">
                        </TestSummary>
                    </template>
                    <!-- Tournament Steps -->
                    <template v-else-if="type === 'tournament'">
                        <TournamentBasicInfo v-if="currentStep == 1" :key="1" :stepActive="true"
                            :setNextBtnDisabled="setNextBtnDisabled"></TournamentBasicInfo>
                        <TournamentDetails v-if="currentStep == 2" :key="2" :stepActive="true"
                            :setNextBtnDisabled="setNextBtnDisabled" :clubId="clubId" />
                        <ParticipantSettings v-if="currentStep == 3" :key="3" :stepActive="true"
                            :setNextBtnDisabled="setNextBtnDisabled" :clubId="clubId"/>
                        <TournamentRules v-if="currentStep == 4" :key="4" :stepActive="true"
                            :setNextBtnDisabled="setNextBtnDisabled" :clubId="clubId"/>
                        <TournamentAwards v-if="currentStep == 5" :key="5" :stepActive="true"
                            :setNextBtnDisabled="setNextBtnDisabled" />
                        <TournamentSummary v-if="currentStep == 6" :key="6" :stepActive="true"
                            :setNextBtnDisabled="setNextBtnDisabled" />
                    </template>
                </transition-group>
            </div>
            <div class="controls">
                <button @click="prevStep()" v-if="currentStep > 1" :disabled="currentStep <= 1" class="prev-step">{{
                    $t('prevStep') }}</button>
                <button @click="nextStep()" class="next-step" :disabled="nxtBtnDisabled">{{ currentStep === steps ?
                    $t(wizardTypes[type].createButtonText) : $t('nextStep') }}</button>
            </div>
        </div>
        <LoadingOverlay style="position: fixed;" :loading="loading"></LoadingOverlay>
        <v-snackbar :color="msgType == 'success' ? 'success' : 'error'" v-model="showMsg">
            {{ msgTxt }}
        </v-snackbar>
    </div>
</template>

<script>
import Stepper from './Stepper.vue';
import ChooseMarket from './steps/ChooseMarket.vue';
import TestToTakeVue from './steps/TestToTake.vue';
import ChooseBroker from './steps/ChooseBroker.vue';
import TestSummary from './steps/TestSummary.vue';
import LoadingOverlay from '../../../../../components/LoadingOverlay.vue';
import TournamentBasicInfo from './steps/TournamentBasicInfo.vue';
import TournamentDetails from './steps/TournamentDetails.vue';
import ParticipantSettings from './steps/ParticipantSettings.vue';
import TournamentRules from './steps/TournamentRules.vue';
import TournamentAwards from './steps/TournamentAwards.vue';
import TournamentSummary from './steps/TournamentSummary.vue';
import { mapMutations } from 'vuex';

import FundingRepository from '../../../../../repositories/funding';
import TournamentRepository from '@/repositories/tournament';

export default {
    components: {
        Stepper,
        ChooseMarket,
        TestToTakeVue,
        ChooseBroker,
        TestSummary,
        LoadingOverlay,
        TournamentBasicInfo,
        TournamentDetails,
        ParticipantSettings,
        TournamentRules,
        TournamentAwards,
        TournamentSummary
    },
    props: {
        fromButton: Boolean,
        hideWizard: Function,
        openWizard: Boolean,
        type: {
            type: String,
            default: 'funding',
            validator: value => ['funding', 'tournament'].includes(value)
        },
        clubId: {
            type: [Number, String],
            default: null
        }
    },
    watch: {
        currentMarket: async function () {
            await this.setChallenges();
        },
        isReverse(value) {
            // Add or remove the is-reverse class to modify animation direction
            const slideElements = document.querySelectorAll('.slide-enter, .slide-leave-to');
            slideElements.forEach(el => {
                if (value) {
                    el.classList.add('is-reverse');
                } else {
                    el.classList.remove('is-reverse');
                }
            });
        }
    },
    data: function () {
        return {
            currentStep: 1,
            currentMarket: undefined,
            currentVendor: undefined,
            currentChallenge: undefined,
            currentPhase: undefined,
            currentBroker: undefined,
            nxtBtnDisabled: true,
            challenges: undefined,
            maxStep: 1,
            loading: false,
            msgType: "success",
            msgTxt: "",
            showMsg: false,
            wizardTypes: {
                funding: {
                    steps: 4,
                    createFunction: this.createFundingTest,
                    goBackText: 'goBackSimulations',
                    createButtonText: 'createSimulation'
                },
                tournament: {
                    steps: 6,
                    createFunction: this.createTournament,
                    goBackText: 'goBackTournaments',
                    createButtonText: 'createTournament'
                }
            },
            isReverse: false
        }
    },
    computed: {
        steps() {
            return this.wizardTypes[this.type].steps;
        }
    },
    created() {
        if (!this.fromButton) {
            this.$router.push({ name: 'funding-simulation-home' });
        }
        if (this.type === 'tournament') {
            this.resetTournamentForm();
        }
    },
    methods: {
        ...mapMutations(['resetTournamentForm']),
        setCurrentMarket(market) {
            this.currentMarket = market;
        },
        setCurrentStep(step) {
            this.currentStep = step;
        },
        setNextBtnDisabled(disabled) {
            this.nxtBtnDisabled = disabled;
        },
        setCurrentVendor(vendor) {
            this.currentVendor = vendor;
        },
        setCurrentPhase(phase) {
            if (phase) {
                if (phase.order <= this.currentChallenge.lastPhase)
                    this.currentPhase = phase;
            } else {
                this.currentPhase = undefined;
            }
        },
        setCurrentChallenge(challenge) {
            this.currentChallenge = challenge;
        },
        setCurrentBroker(broker) {
            this.currentBroker = broker
        },
        async setChallenges() {

            let vendorChallenges = [];
            this.currentChallenge = undefined;
            this.currentPhase = undefined;
            this.currentVendor = undefined;
            this.maxStep = 1;
            await FundingRepository.challengesByMarket(this.currentMarket).then(res => {
                this.challenges = res.data;
            });
            this.challenges.forEach(challenge => {
                let currentChallenge = challenge.challenge;
                if (challenge.challengePhases.length > 0 && challenge.challengePhases.length <= 3) {
                    let currentVendor = vendorChallenges.find(vendor => vendor.id === currentChallenge.fundingVendor.id);
                    challenge.challengePhases.forEach(p => {
                        let newRules = [{
                            fundingRuleType: "TARGET",
                            value: p.target
                        },
                        {
                            fundingRuleType: "CONSISTENCY",
                            value: p.rules.find(r => r.fundingRuleType == "CONSISTENCY") ? this.$t('yes_Wizard') : this.$t('no')
                        },
                        {
                            fundingRuleType: "PERIOD_IN_DAYS",
                            value: p.periodInDays == 180 ? this.$t('unlimited') : p.periodInDays
                        }]
                        p.rules = [...newRules, ...p.rules];
                    });
                    if (currentVendor) {
                        let newChallengesArray = currentVendor.challenges;
                        let newChallengesValuesArray = currentVendor.challengesValues;
                        newChallengesValuesArray.push(currentChallenge.name.match(/([0-9]+)k/)[1] + 'K');
                        newChallengesArray.push({
                            value: currentChallenge.name.match(/([0-9]+)k/)[1] + 'K',
                            name: currentChallenge.name,
                            periodCost: currentChallenge.periodCost,
                            phases: challenge.challengePhases,
                            lastPhase: currentChallenge.lastPhase != null ? currentChallenge.lastPhase : 1
                        });
                        currentVendor.challenges = newChallengesArray;
                    } else {
                        let objToPush = {};
                        let challenges = [];
                        let challengesValues = [];
                        challengesValues.push(currentChallenge.name.match(/([0-9]+)k/)[1] + 'K');
                        challenges.push({
                            value: currentChallenge.name.match(/([0-9]+)k/)[1] + 'K',
                            name: currentChallenge.name,
                            periodCost: currentChallenge.periodCost,
                            phases: challenge.challengePhases,
                            lastPhase: currentChallenge.lastPhase != null ? currentChallenge.lastPhase : 1
                        });
                        objToPush.id = currentChallenge.fundingVendor.id;
                        objToPush.logoUrl = currentChallenge.fundingVendor.logoUrl;
                        objToPush.name = currentChallenge.fundingVendor.name;
                        objToPush.challenges = challenges;
                        objToPush.challengesValues = challengesValues;
                        vendorChallenges.push(objToPush);
                    }
                }
            });
            this.challenges = vendorChallenges;

        },
        async nextStep() {
            this.isReverse = false;
            if (this.currentStep >= 1 && this.currentStep < this.steps) {
                this.currentStep = this.currentStep + 1;
                if (this.currentStep > this.maxStep) {
                    this.maxStep++;
                }

            } else if (this.currentStep == this.steps) {
                await this.wizardTypes[this.type].createFunction();
            }
        },
        prevStep() {
            this.isReverse = true;
            if (this.currentStep > 1 && this.currentStep <= this.steps) {
                this.currentStep = this.currentStep - 1;
            }
        },
        async createFundingTest() {
            let currentCycleEndTime = new Date();
            let additionalMonths = this.currentPhase.periodInDays / 30;
            currentCycleEndTime = new Date(currentCycleEndTime.setMonth(currentCycleEndTime.getMonth() + additionalMonths));
            this.loading = true;
            let fundingTest = {
                createdAt: new Date(),
                currentCycleStartTime: new Date(),
                currentCycleEndTime: currentCycleEndTime,
                participantStatus: 'ACTIVE',
                currentProfit: 0,
                currentSpending: Number(this.currentChallenge.periodCost),
                fundingChallengePhase: { id: this.currentPhase.id },
                brokerAccount: {
                    id: this.currentBroker.id, user: { id: this.$store.state.user.id },
                },
            };

            await FundingRepository.createFundingTest(fundingTest).then((res) => {
                this.showMsg = true;
                this.msgType = "success";
                this.msgTxt = this.$t('msgSuccess');
                this.setNextBtnDisabled(true);
                setTimeout(() => {
                    this.$router.push({ name: 'funding-simulation-summary', params: { testId: res.data.id, market: this.currentMarket } });
                }, 4e3);
            }).catch(() => {
                this.showMsg = true;
                this.msgType = "error";
                this.msgTxt = this.$t('msgError');
            });
        },
        async createTournament() {
            this.loading = true;
            try {
                const form = this.$store.state.tournamentForm;

                // Create datetime objects treating user input as local time
                const startDateTime = new Date(`${form.basicInfo.startDate}T${form.basicInfo.startTime || '09:00'}`);
                const endDateTime = new Date(`${form.basicInfo.endDate}T${form.basicInfo.endTime || '18:00'}`);

                // Determine the club ID to use:
                // 1. First check if a community was selected in the wizard (broker app multi-community flow)
                // 2. Then check clubMember.club.id (traders app flow or broker entering from specific community)
                // 3. Finally fall back to the clubId prop (legacy support)
                const creatorClubId = form.basicInfo.selectedClubId
                    || this.$store.state?.clubMember?.club?.id
                    || this.clubId;

                const tournamentData = {
                    withWoocomerce: form.details.selfManaged,
                    tournament: {
                        creator: {
                            id: creatorClubId
                        },
                        name: form.basicInfo.name,
                        startTime: startDateTime.toISOString(),
                        endTime: endDateTime.toISOString(),
                        createdAt: new Date().toISOString(),
                        tournamentVisibility: form.details.visibility,
                        tournamentRankingType: form.participants.rankingType,
                        initialBalance: form.initialBalance,
                        antiSpoiler: false,
                        inviteOnly: form.participants.inviteOnly,
                    },
                    tournamentSettings: {
                        restrictedAccountType: form.details.tournamentType, // Use tournament type from details
                        faqsUrl: form.basicInfo.faqsLink || '',
                        tournamentVisibility: form.details.visibility,
                        tournamentRankingType: form.participants.rankingType,
                        initialBalance: parseFloat(form.initialBalance),
                        daysPostRegister: this.getRegistrationDays(form.participants.registrationPeriod,startDateTime,endDateTime),
                        antiSpoiler: false,
                        inviteOnly: form.participants.inviteOnly,
                        platform: form.details.platform || null, // Platform code
                        // Conditionally send either metatraderServerGroupId or platformGroupId based on what's available
                        metatraderServerGroupId: form.details.mtGroupId ? {
                            id: form.details.mtGroupId
                        } : null,
                        platformServerGroup: form.details.platformGroupId && !form.details.mtGroupId ? {
                            id: form.details.platformGroupId
                        } : null,
                        publicPerformance: form.details.publicPerformance === 1,
                        mainLanguage: form.basicInfo.mainLanguage || 'en',
                        autoEnroll: form.details.autoEnroll !== undefined ? form.details.autoEnroll : false
                    },
                    tournamentRules: form.rules.map(rule => ({
                        ruleType: rule.type,
                        value: parseFloat(rule.value)
                    })),
                    tournamentAwards: form.awards.map((award, index) => ({
                        rankNumber: index + 1,
                        title: award.name,
                        description: award.description,
                        estimatedValue: award.value.toString()
                    })),
                    selectedFeatures: this.buildSelectedFeatures(form.details.autoEnroll)
                };

                await TournamentRepository.createFullTournamentSettings(tournamentData).then((res) => {
                    this.showMsg = true;
                    this.msgType = "success";
                    this.msgTxt = this.$t('tournamentMsgSuccess');
                    this.setNextBtnDisabled(true);
                    setTimeout(() => {
                        this.$router.push({ name: 'tournament-overview', query: { tournamentId: Number.parseInt(res.data.tournament.id) } });
                    }, 4e3);
                }).catch(() => {
                    this.showMsg = true;
                    this.msgType = "error";
                    this.msgTxt = this.$t('tournamentMsgError');
                });

            } catch (error) {
                //eslint-disable-next-line
                console.error('Error creating tournament:', error);
                this.showMsg = true;
                this.msgType = "error";
                this.msgTxt = this.$t('tournamentMsgError');
            } finally {
                this.loading = false;
            }
        },
        getRegistrationDays(period, startDateTime, endDateTime) {
            const MS_PER_DAY = 1000 * 60 * 60 * 24;
            const tournamentDays = () => {
                if (!startDateTime || !endDateTime) return 0;
                const diffMs = endDateTime.getTime() - startDateTime.getTime();
                if (diffMs <= 0) return 0;
                return Math.ceil(diffMs / MS_PER_DAY); 
            };

            switch (period) {
                case 'ONE_DAY': return 1;
                case 'THREE_DAYS': return 3;
                case 'FIVE_DAYS': return 5;
                case 'ALWAYS': return tournamentDays();
                default: return 0;
            }
        },
        buildSelectedFeatures(autoEnroll) {
            const baseFeatures = [
                "TOURNAMENT_RANKING_AWARD",
                "TOURNAMENT_RANKING_SPONSORED",
                "TOURNAMENT_RANKING_RULES",
                "TOURNAMENT_RANKING_FAQS",
                "TOURNAMENT_RANKING_COUNTER",
                "TOURNAMENT_RANKING_NAME",
                "TOURNAMENT_RANKING_USERNAME",
                "TOURNAMENT_RANKING_ACCOUNT_IDENTIFIER",
                "TOURNAMENT_RANKING_FULL_PAGINATION"
            ];

            // Add TOURNAMENT_RANKING_SHOW_REGISTER when user self-manages accounts (autoEnroll === false)
            // This shows the registration UI when autoEnroll is not available/enabled
            if (autoEnroll === false) {
                baseFeatures.push("TOURNAMENT_RANKING_SHOW_REGISTER");
            }

            return baseFeatures;
        },
        restartWizard() {
            this.hideWizard();
            this.currentStep = 1;
            this.maxStep = 1;
            if (this.type === 'funding') {
                this.currentMarket = undefined;
            } else if (this.type === 'tournament') {
                this.resetTournamentForm();
            }
        },
        goToTournament(tournament) {
            this.$router.push({
                name: 'tournament-overview',
                query: { tournamentId: Number.parseInt(tournament.id) }
            });
        },
    }
}
</script>

<style lang="scss">
.theme--dark {
    .simulation-wizard {
        .header {
            .header__text {
                color: #4E5966;
            }
        }

        .controls {
            button {
                &.prev-step {
                    background: #303740;
                }

                &:disabled {
                    background: #4E5966;
                }
            }
        }
    }

}

.simulation-wizard {
    padding: 40px;
    height: 100vh;
    overflow-y: auto;

    .wizard-content {
        display: flex;
        flex-direction: column;
        min-height: calc(100vh - 120px); // Account for padding

        .header {
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            margin-bottom: 56px;

            .header__text {
                color: #9A9A9A;
                font-size: 16px;
                font-family: "Avenir", sans-serif;

                &:hover {
                    cursor: pointer;
                }

                i {
                    margin-right: 10px;
                }
            }

            @media (max-width: 768px) {
                flex-direction: column;
                margin-bottom: 40px;

                .stepper {
                    margin: auto;
                    margin-top: 40px;
                }
            }
        }

        .body {
            flex: 1;
            position: relative;
            width: calc(100% - 194px);
            margin: 0 47px 40px;
            overflow: hidden; // Add this to contain sliding animations

            .steps-container {
                width: 100%;
                position: relative;

                >* {
                    width: 100%;
                    position: relative; // Changed from absolute
                    left: 0;
                    right: 0;
                }
            }

            @media (max-width: 768px) {
                margin-left: 40px;
                margin-right: 40px;
                width: calc(100% - 80px);

                .choose-market {
                    align-items: center;

                    .markets-container {
                        flex-direction: column;

                        .market-card {
                            width: 100%;
                            min-width: unset;
                            justify-content: center;
                            margin-bottom: 20px;

                            .market-bubble {
                                display: none;
                            }
                        }
                    }
                }
            }

        }

        .controls {
            position: relative; // Changed from static
            width: calc(100% - 194px);
            margin: 0 47px 40px;
            display: flex;
            justify-content: flex-end; // Changed from space-between
            align-items: center;

            @media (max-width: 768px) {
                margin-left: 40px;
                margin-right: 40px;
                width: calc(100% - 80px);
                flex-direction: column-reverse;
                gap: 16px; // Add spacing between stacked buttons
            }

            button {
                color: var(--v-mainBlack-base);
                background: var(--v-mainGreen-base);
                padding: 10px 16px;
                border-radius: 10px;
                border: 1px solid transparent;
                min-width: 100px;

                @media (max-width: 768px) {
                    width: 100%;
                }

                &.prev-step {
                    margin-right: auto; // This pushes next button to right when prev exists
                    background: var(--v-cardHeaderBackground-base);

                    @media (max-width: 768px) {
                        margin: 0;
                    }
                }

                &.next-step {
                    // No specific margin needed
                    margin-left: 0; // Remove auto margin

                    @media (max-width: 768px) {
                        margin: 0;
                        margin-bottom: 10px;
                    }
                }

                &:disabled {
                    background: #9A9A9A;
                }
            }
        }
    }

    @media (max-width: 768px) {
        padding-left: 0;
        padding-right: 0;

        .wizard-content {

            .body,
            .controls {
                width: calc(100% - 80px);
                margin-left: 40px;
                margin-right: 40px;
            }
        }
    }
}

.slide-enter-active,
.slide-leave-active {
    position: absolute;
    width: 100%;
    transition: all 0.3s ease-in-out;
    z-index: 1; // Ensure sliding content stays above
}

.slide-enter {
    opacity: 0;
    transform: translateX(50%);
}

.slide-leave-to {
    opacity: 0;
    transform: translateX(-50%);
}

.slide-enter.is-reverse {
    transform: translateX(-50%);
}

.slide-leave-to.is-reverse {
    transform: translateX(50%);
}
</style>